import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Text,
  Input,
  Field,
  Button,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import { SearchRegular, DismissCircleRegular, ChevronRight16Regular, ChevronDown16Regular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import SidePanel from '../shared/SidePanel';
import { connectorsService } from '../../api';
import { ConnectorIcon } from '../../utils/connectorIcons';
import { isProduction } from '../../utils/environment';
import type { Connector, ConnectorCategory } from '../../api/types';
import { getPickerConnectors, getExpandedCategories } from './connectorPickerState';

const useStyles = makeStyles({
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    ...shorthands.padding('8px', '12px'),
    ...shorthands.borderRadius('6px'),
    cursor: 'pointer',
    marginTop: '8px',
    transitionProperty: 'background-color',
    transitionDuration: '120ms',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  categoryChevron: {
    color: tokens.colorNeutralForeground4,
    flexShrink: 0,
  },
  categoryTitle: {
    fontSize: '12px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground2,
  },
  categoryCount: {
    fontSize: '10px',
    color: tokens.colorNeutralForeground4,
    marginLeft: 'auto',
  },
  connectorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('7px', '12px', '7px', '24px'),
    ...shorthands.borderRadius('8px'),
    cursor: 'pointer',
    transitionProperty: 'background-color',
    transitionDuration: '120ms',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  connectorRowDisabled: {
    opacity: 0.3,
    cursor: 'not-allowed',
    ':hover': {
      backgroundColor: 'transparent',
    },
  },
  connectorInfo: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  },
  connectorName: {
    fontSize: '13px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground1,
  },
  connectorDesc: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '180px',
  },
  comingSoon: {
    fontSize: '9px',
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
    flexShrink: 0,
  },
  loadingCenter: {
    display: 'flex',
    justifyContent: 'center',
    padding: '32px',
  },
});

interface ConnectorPickerPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const ConnectorPickerPanel: React.FC<ConnectorPickerPanelProps> = ({ isOpen, onClose }) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [categories, setCategories] = useState<ConnectorCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const [cats, conns] = await Promise.all([
          connectorsService.getCategories(),
          connectorsService.getConnectors(),
        ]);
        if (!cancelled) {
          setCategories(cats);
          setConnectors(conns);
          setExpandedGroups(getExpandedCategories(cats));
        }
      } catch (err) {
        console.error('Failed to load connectors:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isOpen]);

  const toggleGroup = useCallback((id: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Only show supported, enabled connectors, preserving catalog order.
  const visibleConnectors = useMemo(
    () => getPickerConnectors(connectors, isProduction()), [connectors],
  );

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return visibleConnectors;
    const q = searchQuery.toLowerCase();
    return visibleConnectors.filter(
      (c) => c.name.toLowerCase().includes(q) || (c.description ?? '').toLowerCase().includes(q),
    );
  }, [visibleConnectors, searchQuery]);

  // When searching, expand all groups
  const isSearching = searchQuery.trim().length > 0;

  const grouped = useMemo(() => {
    const map = new Map<string, { category: ConnectorCategory; items: Connector[] }>();
    for (const cat of categories) {
      map.set(cat.id, { category: cat, items: [] });
    }
    for (const conn of filtered) {
      const group = map.get(conn.category.id);
      if (group) group.items.push(conn);
    }
    return Array.from(map.values()).filter((g) => g.items.length > 0);
  }, [filtered, categories]);

  const handleSelect = (connector: Connector) => {
    if (!connector.enabled) return;
    onClose();
    navigate(`/data-sources/new/connect?connector=${connector.id}`);
  };

  return (
    <SidePanel isOpen={isOpen} onClose={onClose} title="Connect Data Source">
      <Field>
        <Input
          size="medium"
          contentBefore={<SearchRegular />}
          contentAfter={searchQuery ? (
            <Button
              appearance="subtle"
              icon={<DismissCircleRegular />}
              size="small"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            />
          ) : undefined}
          placeholder="Search connectors..."
          value={searchQuery}
          onChange={(_, data) => setSearchQuery(data.value)}
        />
      </Field>

      {isLoading ? (
        <div className={styles.loadingCenter}>
          <Spinner size="small" />
        </div>
      ) : (
        grouped.map((group) => {
          const isExpanded = isSearching || expandedGroups.has(group.category.id);
          return (
            <div key={group.category.id}>
              <div
                className={styles.categoryHeader}
                onClick={() => toggleGroup(group.category.id)}
                role="button"
                aria-expanded={isExpanded}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroup(group.category.id); } }}
              >
                {isExpanded
                  ? <ChevronDown16Regular className={styles.categoryChevron} />
                  : <ChevronRight16Regular className={styles.categoryChevron} />
                }
                <Text className={styles.categoryTitle}>{group.category.name}</Text>
                <Text className={styles.categoryCount}>{group.items.length}</Text>
              </div>
              {isExpanded && group.items.map((conn) => (
                <div
                  key={conn.id}
                  className={mergeClasses(styles.connectorRow, !conn.enabled && styles.connectorRowDisabled)}
                  onClick={() => handleSelect(conn)}
                  role="button"
                  aria-disabled={!conn.enabled}
                  tabIndex={conn.enabled ? 0 : -1}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelect(conn); } }}
                >
                  <ConnectorIcon connectorId={conn.id} name={conn.name} size={18} />
                  <div className={styles.connectorInfo}>
                    <Text className={styles.connectorName}>{conn.name}</Text>
                    {conn.description && (
                      <Text className={styles.connectorDesc}>{conn.description}</Text>
                    )}
                  </div>
                  {!conn.enabled && (
                    <Text className={styles.comingSoon}>Coming soon</Text>
                  )}
                </div>
              ))}
            </div>
          );
        })
      )}
    </SidePanel>
  );
};

export default ConnectorPickerPanel;